import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0User } from '@hooks/useAuth0User';
import { useDeviceOs } from '@hooks/useDeviceOs';
import { useIsAppView } from '@hooks/useIsAppView';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@lib/constants';
import { isUserAuthenticated } from '@lib/utils';
import {
  getStoredAppUpsell,
  setStoredAppUpsell
} from '@lib/app-upsell-storage';

const MODAL_REPEAT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

export type AppUpsellState = 'hidden' | 'modal' | 'banner';
export type AppUpsellCtaLabel = 'Get the app' | 'Update';

/**
 * Decides which mobile-app upsell surface (if any) a My Team visit gets.
 *
 * Eligible audience: logged-in members on a mobile device or inside the
 * app's webview, with the PostHog flag on. Logged-out users never see it
 * (My Team redirects them away regardless). First eligible visit shows the modal
 * and stamps localStorage; within 7 days of that stamp each My Team
 * navigation shows the banner instead; after 7 days the modal returns and
 * the stamp resets. The stamp is written on show (not dismiss) so closing
 * the tab mid-modal still counts as shown.
 *
 * Dismissing the banner is also remembered: it stays hidden for the rest
 * of the current 7-day cycle and returns when the modal next shows.
 *
 * The hook decides once per mount. Per-navigation behaviour comes from
 * _app rendering the My Team subtree inside an ErrorBoundary keyed by
 * router.pathname, which remounts this hook on every page change.
 */
export const useAppUpsell = (): {
  upsellState: AppUpsellState;
  storeUrl: string;
  ctaLabel: AppUpsellCtaLabel;
  isAppView: boolean;
  dismiss: () => void;
} => {
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { isIos, isMobile } = useDeviceOs();
  const { isAppView } = useIsAppView();
  const isUpsellEnabled = usePostHogFeatureFlag(
    PostHogFeatureFlags.appUpsellIsLive,
    false
  );

  const [upsellState, setUpsellState] = useState<AppUpsellState>('hidden');
  // Latches once a surface has been shown so re-renders (or the flag/user
  // settling after the decision) can't re-trigger it, while still letting
  // late-arriving flag/user data make the first positive decision.
  const hasDecidedRef = useRef(false);

  useEffect(() => {
    if (hasDecidedRef.current || isUserLoading) return;

    const isEligible =
      isUpsellEnabled && isUserAuthenticated(user) && (isMobile || isAppView);
    if (!isEligible) return;

    hasDecidedRef.current = true;

    const stored = getStoredAppUpsell();
    const now = Date.now();
    // A future timestamp (clock skew) yields a negative elapsed time and
    // falls through to the banner rather than re-showing the modal.
    if (
      stored === null ||
      now - stored.modalLastShownAt >= MODAL_REPEAT_INTERVAL_MS
    ) {
      setStoredAppUpsell({ modalLastShownAt: now });
      setUpsellState('modal');
      return;
    }

    const isBannerDismissedThisCycle =
      stored.bannerDismissedAt !== undefined &&
      stored.bannerDismissedAt >= stored.modalLastShownAt;
    if (!isBannerDismissedThisCycle) {
      setUpsellState('banner');
    }
  }, [isUpsellEnabled, user, isUserLoading, isMobile, isAppView]);

  const dismiss = useCallback(() => {
    if (upsellState === 'banner') {
      const stored = getStoredAppUpsell();
      if (stored) {
        setStoredAppUpsell({ ...stored, bannerDismissedAt: Date.now() });
      }
    }
    setUpsellState('hidden');
  }, [upsellState]);

  return {
    upsellState,
    storeUrl: isIos ? APP_STORE_URL : GOOGLE_PLAY_URL,
    ctaLabel: isAppView ? 'Update' : 'Get the app',
    isAppView,
    dismiss
  };
};
